import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BrainCircuit, ArrowLeft, ChartBar as BarChart3, Search, Target, Rocket, Settings, Lock, Users, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import { useLlmJobsPoll } from '../../lib/useLlmJobsPoll';
import { useAccountRole } from '../../lib/useAccountRole';
import type { Account, OrgConfig, ValuePyramid, ValueStatements, UseCaseRecord } from '../../types';
import { resolveLogoUrl } from '../../lib/logo';
import { buildAdminOrgUrl } from '../../lib/adminApp';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import OverviewTab from '../../components/account/OverviewTab';
import ResearchTab from '../../components/account/ResearchTab';
import PlanTab from '../../components/account/PlanTab';
import ExecuteTab from '../../components/account/ExecuteTab';
import AccountTeamPanel from '../../components/account/AccountTeamPanel';
import MavisAccountTab from '../../components/mavis/MavisAccountTab';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'research', label: 'Research', icon: Search },
  { id: 'plan', label: 'Plan', icon: Target },
  { id: 'execute', label: 'Execute', icon: Rocket },
  // MAVIS is a beta feature: shown only to organisations with beta features enabled.
  { id: 'mavis', label: 'MAVIS', icon: BrainCircuit, requiresBeta: true },
];

function isOrgConfigComplete(config: OrgConfig | null): boolean {
  if (!config) return false;
  return !!(config.vendor_name?.trim() && config.vendor_url?.trim() && config.vendor_docs_url?.trim());
}

export default function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { organisation, membership } = useAuth();
  const { apiGet } = useApi();
  const { role: accountRole, isOwner, canEdit, loading: roleLoading } = useAccountRole(id);
  const [account, setAccount] = useState<Account | null>(null);
  const [showTeamPanel, setShowTeamPanel] = useState(false);
  const [teamCount, setTeamCount] = useState(0);
  const [pyramid, setPyramid] = useState<ValuePyramid | null>(null);
  const [valueStatements, setValueStatements] = useState<ValueStatements | null>(null);
  const [useCaseRecord, setUseCaseRecord] = useState<UseCaseRecord | null>(null);
  const [vendorIntelPublished, setVendorIntelPublished] = useState(false);
  const [orgConfig, setOrgConfig] = useState<OrgConfig | null>(null);
  const betaEnabled = organisation?.beta_features_enabled ?? false;
  const visibleTabs = TABS.filter(tab => !tab.requiresBeta || betaEnabled);
  const requestedTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(visibleTabs.some(tab => tab.id === requestedTab) ? requestedTab : 'overview');
  const [deepContactId] = useState(searchParams.get('contactId') || null);
  const [loading, setLoading] = useState(true);

  const pyramidProcessing = pyramid?.status === 'processing';
  const vsProcessing = valueStatements?.status === 'processing';

  // Drive the generic background-LLM poll while any account-level flow is in flight, so its
  // background run (discover-partners, strategic-goals, value-pyramid, ...) is retrieved and
  // finalized. The endpoint is org-scoped and drains every in-flight job, so this single
  // mount advances all of the account's flows. Contact-level flows keep their own mounts.
  const anyLlmJobProcessing =
    account?.enrichment_status === 'processing'
    || account?.partners_status === 'processing'
    || account?.signals_status === 'processing'
    || account?.department_research_status === 'processing'
    || pyramidProcessing
    || pyramid?.strategic_goals_status === 'processing'
    || vsProcessing
    || useCaseRecord?.status === 'processing';
  useLlmJobsPoll(organisation?.id, anyLlmJobProcessing);
  // Department research is owned by a different lambda, so it exposes (and is drained through)
  // its own check endpoint.
  useLlmJobsPoll(
    organisation?.id,
    account?.department_research_status === 'processing',
    '/v1/research-departments/llm-jobs/check',
  );

  const refreshAccount = useCallback(async () => {
    if (!id || !organisation) return;
    try {
      const { account: fresh } = await apiGet<{ account: Account }>(`/v1/account/${id}?org_id=${organisation.id}`);
      if (fresh) setAccount(fresh);
    } catch { /* ignore */ }
  }, [id, organisation, apiGet]);

  const loadData = useCallback(async () => {
    if (!id || !organisation) return;
    try {
      const { account: fetchedAccount } = await apiGet<{ account: Account | null }>(
        `/v1/account/${id}?org_id=${organisation.id}`,
      );
      if (!fetchedAccount) { setLoading(false); return; }
      setAccount(fetchedAccount);
    } catch {
      setLoading(false);
      return;
    }

    const [valueRes, configRes, viRes] = await Promise.all([
      apiGet<{ pyramid: ValuePyramid | null; value_statements: ValueStatements | null; use_cases: UseCaseRecord | null }>(`/v1/account/${id}/value?org_id=${organisation.id}`).catch(() => ({ pyramid: null, value_statements: null, use_cases: null })),
      apiGet<{ config: OrgConfig | null }>(`/v1/org-config?org_id=${organisation.id}`).catch(() => ({ config: null })),
      apiGet<{ intelligence: { status: string; is_published: boolean } | null }>(`/v1/vendor-intelligence?org_id=${organisation.id}`).catch(() => ({ intelligence: null })),
    ]);
    if (valueRes.pyramid) setPyramid(valueRes.pyramid);
    if (valueRes.value_statements) setValueStatements(valueRes.value_statements);
    if (valueRes.use_cases) setUseCaseRecord(valueRes.use_cases);
    if (configRes.config) setOrgConfig(configRes.config);
    setVendorIntelPublished(viRes.intelligence?.status === 'completed' && viRes.intelligence?.is_published === true);
    setLoading(false);
  }, [id, organisation, apiGet]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || roleLoading) return <div className="flex items-center justify-center h-screen"><LoadingSpinner size="lg" /></div>;
  if (!account) return <div className="p-8 text-center"><p className="text-app-muted">Account not found</p><button onClick={() => navigate('/app')} className="btn-primary mt-4">Back to Accounts</button></div>;

  const configComplete = isOrgConfigComplete(orgConfig);

  const overviewComplete = account.enrichment_status === 'completed';
  const researchComplete = pyramid?.status === 'completed' && valueStatements?.status === 'completed';
  const planComplete = useCaseRecord?.status === 'completed';

  const isViewer = accountRole === 'viewer';
  const tabLocked: Record<string, boolean> = {
    overview: false,
    research: isViewer ? false : !overviewComplete,
    plan: isViewer ? false : (!researchComplete || !configComplete),
    execute: isViewer ? false : (!planComplete || !configComplete),
  };

  const tabLockReason: Record<string, string> = {
    overview: '',
    research: 'Complete the Overview step first',
    plan: !configComplete ? 'Complete Organisation Settings first' : 'Complete Research first',
    execute: !configComplete ? 'Complete Organisation Settings first' : 'Complete the Plan step first',
  };

  const tabProcessing: Record<string, boolean> = {
    overview: account.enrichment_status === 'processing',
    research: pyramidProcessing || vsProcessing,
    plan: useCaseRecord?.status === 'processing',
    execute: false,
  };

  return (
    <div className="min-h-screen bg-app-canvas">
      <div className="sticky top-14 z-20 border-b border-app-line bg-app-surface md:top-0">
        <div className="mx-auto max-w-[1440px] px-4 py-4 sm:px-8">
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4">
              <button onClick={() => navigate('/app')} className="p-1.5 text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
              <div className="flex items-center gap-3">
                <AccountLogo account={account} />
                <div>
                  <p className="app-kicker mb-1">Account workspace</p>
                  <h1 className="truncate text-xl font-semibold text-app-ink">{account.company_name}</h1>
                  <p className="text-xs text-app-muted">{[account.industry, account.hq_location, account.employee_count > 0 ? `${account.employee_count.toLocaleString()} employees` : ''].filter(Boolean).join(' | ')}</p>
                </div>
              </div>
              {accountRole === 'viewer' && (
                <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-app-canvas text-app-muted border border-app-line">
                  Read Only
                </span>
              )}
              {accountRole === 'member' && (
                <span className="text-[10px] font-medium px-2 py-1 rounded-full bg-app-focus/10 text-app-focus border border-app-focus">
                  Team Member
                </span>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowTeamPanel(!showTeamPanel)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-app-muted border border-app-line rounded-lg hover:bg-app-canvas hover:border-app-line-strong transition"
              >
                <Users className="w-4 h-4" />
                Team
                {teamCount > 1 && (
                  <span className="w-5 h-5 bg-forest-100 text-forest-700 text-[10px] font-bold rounded-full flex items-center justify-center">
                    {teamCount}
                  </span>
                )}
              </button>
              {showTeamPanel && account && (
                <AccountTeamPanel account={account} isOwner={isOwner} onClose={() => { setShowTeamPanel(false); loadData(); }} />
              )}
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="Account workspace sections">
            {visibleTabs.map(tab => {
              const locked = tabLocked[tab.id];
              const processing = tabProcessing[tab.id];
              return (
                <button
                  key={tab.id}
                  onClick={() => { if (!locked) setActiveTab(tab.id); }}
                  disabled={locked}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  title={locked ? tabLockReason[tab.id] : ''}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition ${
                    locked
                      ? 'border-transparent text-app-line-strong cursor-not-allowed'
                      : activeTab === tab.id
                        ? 'border-forest-600 text-forest-700 bg-forest-50/50'
                        : 'border-transparent text-app-muted hover:text-app-ink hover:border-app-line-strong'
                  }`}
                >
                  {locked ? <Lock className="w-3.5 h-3.5" /> : <tab.icon className="w-4 h-4" />}
                  {tab.label}
                  {processing && <span className="w-2 h-2 rounded-full bg-forest-500 animate-pulse" />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="app-page">
        {accountRole === 'viewer' && (
          <div className="mb-4 px-4 py-3 bg-app-canvas border border-app-line rounded-lg flex items-center gap-2">
            <Lock className="w-4 h-4 text-app-muted" />
            <p className="text-sm text-app-muted">You are viewing this account as a <span className="font-medium">manager</span>. Editing is restricted to the account owner.</p>
          </div>
        )}
        {activeTab === 'overview' && (
          <OverviewTab account={account} pyramid={pyramid} valueStatements={valueStatements} vendorIntelPublished={vendorIntelPublished} onAccountUpdate={updated => setAccount(updated)} isOwner={isOwner} />
        )}
        {activeTab === 'research' && (
          <ResearchTab
            account={account}
            onAccountRefresh={refreshAccount}
            onPyramidChange={(p) => setPyramid(p)}
            valueStatements={valueStatements}
            onValueStatementsChange={(vs) => setValueStatements(vs)}
            vendorIntelPublished={vendorIntelPublished}
            pyramidCompleted={pyramid?.status === 'completed'}
            isOwner={isOwner}
          />
        )}
        {activeTab === 'plan' && (
          configComplete
            ? <PlanTab account={account} onUseCaseChange={(uc) => setUseCaseRecord(uc)} isOwner={isOwner} />
            : <OrgConfigRequired isAdmin={membership?.role === 'admin'} orgId={organisation?.id} />
        )}
        {activeTab === 'execute' && (
          configComplete
            ? <ExecuteTab account={account} deepContactId={deepContactId} />
            : <OrgConfigRequired isAdmin={membership?.role === 'admin'} orgId={organisation?.id} />
        )}
        {activeTab === 'mavis' && betaEnabled && <MavisAccountTab account={account} canEdit={canEdit} />}
      </div>
    </div>
  );
}

function AccountLogo({ account }: { account: Account }) {
  const [failed, setFailed] = useState(false);
  const logoSrc = resolveLogoUrl(account.logo_url, account.website_url);
  if (!logoSrc || failed) {
    return (
      <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-bold text-forest-600">{account.company_name.charAt(0)}</span>
      </div>
    );
  }
  return <img src={logoSrc} alt="" className="w-10 h-10 rounded-lg object-cover bg-app-canvas flex-shrink-0" onError={() => setFailed(true)} />;
}

function OrgConfigRequired({ isAdmin, orgId }: { isAdmin: boolean; orgId?: string }) {
  return (
    <div className="bg-app-warning/10 border border-app-warning rounded-lg p-8 text-center">
      <Settings className="w-12 h-12 text-app-warning mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-app-warning mb-2">Organisation Configuration Required</h3>
      <p className="text-sm text-app-warning mb-6 max-w-md mx-auto">
        Before using Plan and Execute, your organisation's vendor details must be completed.
        {isAdmin
          ? " Please fill in the Vendor Name, Website URL, and Documentation URL in Organisation Settings."
          : ' Ask your organisation admin to fill in the Vendor Name, Website URL, and Documentation URL.'}
      </p>
      {isAdmin && orgId && (
        <a href={buildAdminOrgUrl(orgId)} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center">
          <Settings className="w-4 h-4 mr-2" /> Go to Organisation Settings
          <ExternalLink className="w-3.5 h-3.5 ml-2" aria-label="Opens in a new tab" />
        </a>
      )}
    </div>
  );
}
